import {t} from 'i18next';
import {IRetryData} from '../../redux/slices/indexingManagementSlice';

type Status =
  | 'InProgress'
  | 'CompletedWithError'
  | 'CompletedWithErrors'
  | 'Completed'
  | 'Pending'
  | 'Failed'
  | 'Not Found';

export enum indexingStatusKey {
  inProgress = 'InProgress',
  InProgress = 'In Progress',
  complete = 'Completed',
  completeWithError = 'CompletedWithError',
  completeWithErrors = 'CompletedWithErrors',
  pending = 'Pending',
  failed = 'Failed',
  queued = 'Queued',
  notFound = 'Not Found',
}

export enum indexingDataType {
  text = 'Text',
}

const STATUS_MAP: Record<Status, string> = {
  InProgress: 'indexingStatus.inProgress',
  CompletedWithError: 'indexingStatus.completedWithError',
  CompletedWithErrors: 'indexingStatus.completedWithError',
  Completed: 'indexingStatus.completed',
  Pending: 'indexingStatus.pending',
  Failed: 'indexingStatus.failed',
  'Not Found': 'indexingStatus.notFound',
};

export function formatStatus(status: Status): string {
  const translationKey = STATUS_MAP[status];
  return translationKey ? t(translationKey) : status;
}

export const isCompletedWithErrorStatus = (
  status: string | undefined
): boolean => {
  const normalizedStatus = status?.replace(/\s/g, '').toLowerCase();
  return (
    normalizedStatus === 'completedwitherror' ||
    normalizedStatus === 'completedwitherrors' ||
    normalizedStatus === 'completewitherror' ||
    normalizedStatus === 'completewitherrors'
  );
};

export function shouldShowLastIndexingEvent(
  status: string | undefined
): boolean {
  return (
    status !== indexingStatusKey.inProgress &&
    status !== indexingStatusKey.notFound
  );
}

export function isIndexingBannerShownByExistingLogic(
  showIndexBanner: boolean,
  indexStatus?: string
): boolean {
  return showIndexBanner || indexStatus === indexingStatusKey.inProgress;
}

export function getIndexingBannerFromIndexType(indexType?: string): {
  message: string;
  showIndexBanner: boolean;
} {
  return {
    message: indexType
      ? `${indexType} ${t('indexes.indexBannerTitle')}`
      : t('indexes.indexBannerTitle'),
    showIndexBanner: true,
  };
}

export function getIndexingBannerFromStatusList(
  data: Array<{type?: string; status?: string}> | null | undefined,
  fallbackIndexType?: string
): {message: string; showIndexBanner: boolean} {
  if (!data || !Array.isArray(data)) {
    return {message: '', showIndexBanner: false};
  }

  const inProgressItems = data.filter(
    (item) => item.status === indexingStatusKey.inProgress
  );
  const inProgressCount = inProgressItems.length;

  let message = '';
  if (inProgressCount > 1) {
    message = t('indexes.multipleIndexBannerTitle');
  } else if (inProgressCount === 1) {
    const indexType = inProgressItems[0]?.type || fallbackIndexType || '';
    message = indexType
      ? `${indexType} ${t('indexes.indexBannerTitle')}`
      : t('indexes.indexBannerTitle');
  }

  return {
    message,
    showIndexBanner: inProgressCount > 0,
  };
}

export enum CustomIndexAnalyzersFormInputKey {
  customName = 'customName',
  stopWords = 'stopWords',
  caseConversionFlag = 'caseConversionFlag',
  whitespaceCharList = 'whitespaceCharList',
}

export enum IndexesType {
  standard = 'Standard',
  nlp = 'Nlp',
  custom = 'Custom',
}

export enum datasetName {
  newExport = 'New ExportSet',
  overlay = 'Overlay',
  overlayMediaAPCUpdate = 'Overlay - Media APC Update',
  overlayProjectAPCUpdate = 'Overlay - Project APC Update',
  deleteDocuments = 'Delete Documents',
}

const normalizeIndexEventName = (name?: string): string =>
  name?.replace(/\s/g, '').toLowerCase() ?? '';

const DELETE_DOCUMENTS_EVENT_NAMES = new Set([
  'delete',
  'deletedocuments',
  'deleteddocuments',
  'deleteindexeddocuments',
  'deletedindexeddocuments',
  'documentdeletion',
]);

export const CUSTOM_INDEX_DATA_TYPES = ['Tag', 'STR', 'Export'];

export interface EcaIndexStatus {
  indexId?: number;
  ecaIndexId?: number;
  ecaNlpingestionId?: number;
  datasetName?: string;
  status?: string;
  startDateTime?: string;
  endDateTime?: string;
  totalDocCount?: number;
  endDocCount?: number;
  actedOnDocCount?: number;
  indexedDocCount?: number;
  failedDocCount?: number;
  partialFailedDocCount?: number;
  initiatedByUser?: string;
  ecaExportId?: number;
  fieldTemplateId?: number;
  name?: string;
  id?: number | string;
  parentId?: number;
  type?: string;
  toggleExpansion?: () => void;
  downloadFailed?: boolean;
  showDownloadIcon?: boolean;
  isExpanded?: boolean;
  buildType?: string;
  mediaId?: string;
  custodianName?: string;
  failureReason?: string;
  errorMessage?: string;
  statusMessage?: string;
  reason?: string;
}

export const isDeleteDocumentsEvent = (
  event?: Pick<EcaIndexStatus, 'buildType' | 'datasetName'>
): boolean =>
  DELETE_DOCUMENTS_EVENT_NAMES.has(
    normalizeIndexEventName(event?.datasetName)
  ) ||
  DELETE_DOCUMENTS_EVENT_NAMES.has(normalizeIndexEventName(event?.buildType));

export const toNegativeDocumentCount = (count?: number): number | undefined => {
  if (count === undefined || count === null) {
    return count;
  }

  return count > 0 ? count * -1 : count;
};

export const normalizeDeleteDocumentsEvent = (
  event: EcaIndexStatus,
  isStandardIndex = false
): EcaIndexStatus => {
  if (!isDeleteDocumentsEvent(event)) {
    return event;
  }

  return {
    ...event,
    datasetName: datasetName.deleteDocuments,
    buildType: datasetName.deleteDocuments,
    initiatedByUser: event.initiatedByUser ?? 'System',
    actedOnDocCount: isStandardIndex
      ? toNegativeDocumentCount(event.actedOnDocCount)
      : event.actedOnDocCount,
    indexedDocCount: isStandardIndex
      ? toNegativeDocumentCount(event.indexedDocCount)
      : event.indexedDocCount,
    totalDocCount: isStandardIndex
      ? toNegativeDocumentCount(event.totalDocCount)
      : event.totalDocCount,
  };
};

export interface ISelectedItem {
  id: number;
  name: string;
}

export type ISelectedItems = ISelectedItem[];

export interface IAnalyzerConfig {
  id?: number;
  name: string;
  stopWordsList: string[];
  caseConversionFlag: boolean;
  whitespaceCharList: string;
  analyzer?: string;
  stopwordList: string[];
  alphabeticalCharacters?: string;
  label?: string;
  description?: string;
  type?: string;
  customName?: string;
  stopWords?: string[];
  isVisible?: boolean;
}

export const buildRetryData = (payload?: string): IRetryData => {
  try {
    if (!payload) {
      return {
        custodians: [],
        fields: [],
      };
    }

    const parsed = JSON.parse(payload);

    return {
      custodians:
        parsed.Custodians?.map((custodian: any) => ({
          id: custodian.Id,
          name: custodian.Name,
        })) ?? [],

      fields:
        parsed.Fields?.map((field: any) => ({
          name: field.Name,
          type: field.Type,
          description: field.Description,
          cases: field.Cases ?? [],
          length: field.Length,
          dateTimeFormat: field.DateTimeFormat,
          isCustom: !field.IsStandard, //"isCustom": true If IsStandard false vice versa
          isExportableForReview: field.IsExportableForReview,
        })) ?? [],
    };
  } catch (error) {
    console.error('Failed to parse retry payload', error);

    return {
      custodians: [],
      fields: [],
    };
  }
};

export enum fieldTypes {
  standard = 'standard',
  custom = 'custom'
}
